import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Card, CardBody, Col, Button, Spinner, Row, Input } from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios from "axios";
import useCurrency, { baseUrl } from "../../../Utils/FileUpload";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaFilePdf, FaFileExcel } from "react-icons/fa";
import { exportToPDF } from "../../../Utils/pdfExportUtils";
import { excelExportUtils } from "../../../Utils/excelExportUtils";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../../Context/AuthContext";

interface CallPayout {
  id: number;
  agent_id: number;
  agent_name: string;
  amount: string;
  amount_without_current: string;
  amount_raw?: number;
  callenddate: string;
  callstartdate: string;
  created_at: string;
  date: string;
  is_paid: number | string;
  isrequest: number;
  transaction_id: string;
  updated_at: string;
  isFooter?: boolean;
}

const customStyles = {
  headCells: {
    style: {
      fontSize: "16px",
      fontWeight: "600",
      color: "#308e87",
      backgroundColor: "#f8f9fa",
      borderBottom: "2px solid #308e87",
    },
  },
  rows: { style: { borderBottom: "1px solid #dee2e6" } },
  table: { style: { border: "1px solid #dee2e6", borderRadius: "8px" } },
};

const conditionalRowStyles = [
  {
    when: (row: any) => row.isFooter === true,
    style: {
      fontWeight: "800",
      backgroundColor: "#f8f9fa",
      color: "#308e87",
      borderTop: "2px solid #dee2e6",
      fontSize: "16px",
    },
  },
];

export default function CallPayoutReport() {
  const token = localStorage.getItem("token");
  const { currency } = useCurrency();
  const location = useLocation();
  const { hasPermission } = useAuth();
  const agentId = location.state?.agentId;

  const [filteredItems, setFilteredItems] = useState<CallPayout[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [agents, setAgents] = useState<{ id: number; agent_name: string }[]>([]);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [grandTotals, setGrandTotals] = useState({ amount: 0 });

  let personTitle = "";
  if (agentId && filteredItems.length > 0) {
    personTitle = `${filteredItems[0].agent_name || "Agent"}'s Call Payout`;
  }

  // Fetch agents list
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const res = await axios.get(`${baseUrl}agent/getall`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAgents((res.data || []).filter((a: any) => a.is_deleted === 0 && a.is_active === 1));
      } catch (e) {
        console.error("Error fetching agents:", e);
      }
    };
    fetchAgents();
  }, [token]);

  const fetchData = useCallback(
    async (page = currentPage, pp = perPage) => {
      if (fromDate && toDate && fromDate > toDate) {
        toast.error("From date cannot be greater than To date.");
        return;
      }
      setTableLoading(true);
      try {
        const res = await axios.get(`${baseUrl}call_payroll/get_all`, {
          params: {
            page,
            per_page: pp,
            from_date: fromDate ? fromDate.toISOString().split("T")[0] : null,
            to_date: toDate ? toDate.toISOString().split("T")[0] : null,
            agent_name: selectedAgent || null,
            agent_id: agentId || null,
            sort_field: sortField,
            sort_direction: sortDir,
          },
          headers: { Authorization: `Bearer ${token}` },
        });
        setFilteredItems(res.data.data || []);
        setTotalRows(res.data.total || 0);
        setCurrentPage(res.data.current_page || 1);
        if (res.data.grand_totals) {
          setGrandTotals({ amount: res.data.grand_totals.amount || 0 });
        }
      } catch (e) {
        console.error("Error fetching payout data:", e);
        toast.error("Failed to fetch data");
      } finally {
        setTableLoading(false);
      }
    },
    [currentPage, perPage, fromDate, toDate, selectedAgent, agentId, sortField, sortDir, token]
  );

  useEffect(() => {
    fetchData(currentPage, perPage);
  }, [currentPage, perPage, selectedAgent, fromDate, toDate, sortField, sortDir]);

  const handleSort = useCallback((column: any, direction: "asc" | "desc") => {
    setSortField(column.sortField || "created_at");
    setSortDir(direction);
    setCurrentPage(1);
  }, []);

  const fetchAllForExport = async (): Promise<CallPayout[]> => {
    try {
      const res = await axios.get(`${baseUrl}call_payroll/get_all_without_pagination`, {
        params: {
          from_date: fromDate ? fromDate.toISOString().split("T")[0] : null,
          to_date: toDate ? toDate.toISOString().split("T")[0] : null,
          agent_name: selectedAgent || null,
          agent_id: agentId || null,
          sort_field: sortField,
          sort_direction: sortDir,
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = res.data.data || res.data;
      return Array.isArray(d) ? d : Object.values(d);
    } catch {
      return [];
    }
  };

  const columnsData = useMemo<CallPayout[]>(() => {
    if (filteredItems.length === 0) return [];
    const totalPages = Math.ceil(totalRows / perPage) || 1;
    const isLastPage = currentPage >= totalPages;
    if (!isLastPage) return filteredItems;
    const formattedTotal = (currency || "") + (Number.isInteger(grandTotals.amount) ? grandTotals.amount : grandTotals.amount.toFixed(2));
    return [
      ...filteredItems,
      {
        isFooter: true,
        id: -1 as any,
        agent_id: -1,
        agent_name: "",
        amount: formattedTotal,
        amount_without_current: "",
        amount_raw: grandTotals.amount,
        callstartdate: "",
        callenddate: "",
        created_at: "",
        date: "",
        is_paid: "",
        isrequest: 0,
        transaction_id: "",
        updated_at: "",
      },
    ];
  }, [filteredItems, grandTotals, currentPage, perPage, totalRows, currency]);

  const currencyH = currency ? ` (${currency})` : "";

  const columns = useMemo(() => [
    {
      name: <div>Sl.No</div>,
      cell: (row: CallPayout, index: number) => (row.isFooter ? "" : (currentPage - 1) * perPage + index + 1),
      width: "75px",
    },
    {
      name: <div>Agent Name</div>,
      sortable: true,
      sortField: "agent_name",
      cell: (row: CallPayout) => (row.isFooter ? <strong>Total</strong> : row.agent_name || "N/A"),
    },
    {
      name: <div>Payment Date</div>,
      sortable: true,
      sortField: "date",
      cell: (row: CallPayout) => (row.isFooter ? "" : row.date || "N/A"),
    },
    {
      name: <div>Call Start Date</div>,
      sortable: true,
      sortField: "callstartdate",
      cell: (row: CallPayout) => (row.isFooter ? "" : row.callstartdate || "N/A"),
    },
    {
      name: <div>Call End Date</div>,
      sortable: true,
      sortField: "callenddate",
      cell: (row: CallPayout) => (row.isFooter ? "" : row.callenddate || "N/A"),
    },
    {
      name: <div>Transaction ID</div>,
      sortable: true,
      sortField: "transaction_id",
      cell: (row: CallPayout) => (row.isFooter ? "" : row.transaction_id || "N/A"),
    },
    {
      name: <div>Payment Status</div>,
      cell: (row: CallPayout) => {
        if (row.isFooter) return "";
        const isPaid = row.is_paid === 1;
        return (
          <span
            style={{
              fontWeight: "normal",
              backgroundColor: isPaid ? "#d4edda" : "#f8d7da",
              color: isPaid ? "#155724" : "#721c24",
              padding: "4px 8px",
              borderRadius: "10px",
              display: "inline-block",
            }}
          >
            {isPaid ? "Paid" : "Unpaid"}
          </span>
        );
      },
    },
    {
      name: <div>Amount{currencyH}</div>,
      sortable: true,
      sortField: "amount",
      cell: (row: CallPayout) => (
        <span style={{ fontWeight: row.isFooter ? "700" : "normal" }}>
          {row.isFooter ? row.amount : `${String(row.amount_without_current || "N/A")}`}
        </span>
      ),
    },
  ], [currency, currentPage, perPage, grandTotals]);

  const exportPDF = async () => {
    const allData = await fetchAllForExport();
    if (!allData.length) {
      toast.warn("No data for export.");
      return;
    }
    const currencyMap: Record<string, string> = {
      "₹": "Rs.",
      $: "USD",
      "€": "EUR",
      "£": "GBP",
    };
    const safeCurrency = currencyMap[currency] ?? currency;
    const safeCurrencyHeader = safeCurrency ? ` (${safeCurrency})` : "";

    let total = 0;
    const rows = allData.map((row: any, i: number) => {
      const amt = Number(row.amount_without_current?.replace(/,/g, "") || row.amount_raw || 0);
      total += amt;
      return [
        i + 1,
        row.agent_name || "N/A",
        row.date || "N/A",
        row.callstartdate || "N/A",
        row.callenddate || "N/A",
        row.transaction_id || "N/A",
        row.is_paid === 1 ? "Paid" : "Unpaid",
        amt.toFixed(2),
      ];
    });
    rows.push(["Total", "", "", "", "", "", "", total.toFixed(2)]);
    exportToPDF({
      fileName: "Call_Payout_Report",
      columns: [
        "Sl.No",
        "Agent Name",
        "Payment Date",
        "Call Start Date",
        "Call End Date",
        "Transaction ID",
        "Payment Status",
        `Amount${safeCurrencyHeader}`,
      ],
      data: rows,
      emptyDataMessage: "No payout data available for export.",
    });
  };

  const exportExcel = async () => {
    const allData = await fetchAllForExport();
    if (!allData.length) {
      toast.warn("No data for export.");
      return;
    }
    const currencyHeader = currency ? ` (${currency})` : "";
    let total = 0;
    const tableData = allData.map((row: any, i: number) => {
      const amt = Number(row.amount_without_current?.replace(/,/g, "") || row.amount_raw || 0);
      total += amt;
      return {
        "Sl.No": i + 1,
        "Agent Name": row.agent_name || "N/A",
        "Payment Date": row.date || "N/A",
        "Call Start Date": row.callstartdate || "N/A",
        "Call End Date": row.callenddate || "N/A",
        "Transaction ID": row.transaction_id || "N/A",
        "Payment Status": row.is_paid === 1 ? "Paid" : "Unpaid",
        [`Amount${currencyHeader}`]: Number(amt.toFixed(2)),
      };
    });
    tableData.push({
      "Sl.No": "" as any,
      "Agent Name": "Total",
      "Payment Date": "",
      "Call Start Date": "",
      "Call End Date": "",
      "Transaction ID": "",
      "Payment Status": "",
      [`Amount${currencyHeader}`]: Number(total.toFixed(2)),
    });
    excelExportUtils(tableData, "Call_Payout_Report", "Call Payout Report");
  };

  return (
    <>
      <div className="page-body">
        <Breadcrumbs pageTitle="Call Payout" parent="Payouts" title="Call Payout" />
        <Col xl={12} className="mb-lg-3">
          <Card>
            <CardBody>
              {personTitle && (
                <h5 style={{ marginBottom: "1rem", color: "#308e87", fontWeight: "600" }}>{personTitle}</h5>
              )}
              <Row className="align-items-center justify-content-between mb-3">
                <Col xl={2}>
                  {!agentId && (
                    <div className="d-flex gap-2">
                      {hasPermission("payout-report.download_pdf") && (
                        <Button color="primary" onClick={exportPDF} className="d-flex justify-content-center align-items-center p-0" style={{ width: "30px", height: "30px" }} title="PDF">
                          <FaFilePdf size={16} />
                        </Button>
                      )}
                      {hasPermission("payout-report.download_excel") && (
                        <Button color="secondary" onClick={exportExcel} className="d-flex justify-content-center align-items-center p-0" style={{ width: "30px", height: "30px" }} title="Excel">
                          <FaFileExcel size={16} />
                        </Button>
                      )}
                    </div>
                  )}
                </Col>
                {!agentId && (
                  <Col xl={3}>
                    <Input
                      type="select"
                      value={selectedAgent}
                      onChange={(e) => { setSelectedAgent(e.target.value); setCurrentPage(1); }}
                    >
                      <option value="">Filter by agent</option>
                      {agents.map((agent) => (
                        <option key={agent.id} value={agent.agent_name}>{agent.agent_name}</option>
                      ))}
                    </Input>
                  </Col>
                )}
                <Col xl={6}>
                  <div className="d-flex align-items-center gap-2 justify-content-end">
                    <label className="mb-0">From:</label>
                    <DatePicker
                      selected={fromDate}
                      onChange={(d) => { setFromDate(d); setCurrentPage(1); }}
                      dateFormat="yyyy-MM-dd"
                      className="form-control"
                      placeholderText="Start date"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      isClearable
                    />
                    <label className="mb-0">To:</label>
                    <DatePicker
                      selected={toDate}
                      onChange={(d) => { setToDate(d); setCurrentPage(1); }}
                      dateFormat="yyyy-MM-dd"
                      className="form-control"
                      placeholderText="End date"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      isClearable
                    />
                  </div>
                </Col>
              </Row>
              <div className="table-responsive custom-scrollbar">
                <DataTable
                  data={columnsData}
                  columns={columns}
                  highlightOnHover
                  striped
                  customStyles={customStyles}
                  pagination
                  paginationServer
                  paginationPerPage={perPage}
                  paginationTotalRows={totalRows}
                  onChangePage={(p) => setCurrentPage(p)}
                  onChangeRowsPerPage={(np) => { setPerPage(np); setCurrentPage(1); }}
                  paginationDefaultPage={currentPage}
                  responsive
                  sortServer
                  onSort={handleSort}
                  conditionalRowStyles={conditionalRowStyles}
                  progressPending={tableLoading}
                  progressComponent={
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px", width: "100%" }}>
                      <Spinner color="primary" />
                    </div>
                  }
                />
              </div>
            </CardBody>
          </Card>
        </Col>
        <ToastContainer />
      </div>
    </>
  );
}
